import React, { useState, useRef, useEffect } from 'react';
import {
  ChevronUpIcon,
  ChevronDownIcon,
  FunnelIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';

export type SortDirection = 'asc' | 'desc' | null;
export type FilterType = 'multiselect' | 'searchable' | 'range' | 'daterange';

interface ColumnHeaderProps {
  title: string;
  sortKey: string;
  sortDirection: SortDirection;
  onSort: (key: string) => void;

  // Filter props
  filterType?: FilterType;
  filterOptions?: string[];
  selectedValues?: string[];
  onFilterChange?: (values: string[]) => void;
  onRangeChange?: (min: number | undefined, max: number | undefined) => void;
  onDateRangeChange?: (from: string | undefined, to: string | undefined) => void;

  // Range filter values
  rangeMin?: number;
  rangeMax?: number;
  dateFrom?: string;
  dateTo?: string;

  // Styling
  className?: string;
  width?: string;
}

const ColumnHeader: React.FC<ColumnHeaderProps> = ({
  title,
  sortKey,
  sortDirection,
  onSort,
  filterType,
  filterOptions = [],
  selectedValues = [],
  onFilterChange,
  onRangeChange,
  onDateRangeChange,
  rangeMin,
  rangeMax,
  dateFrom,
  dateTo,
  className = '',
  width,
}) => {
  const [showFilter, setShowFilter] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const filterRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLTableCellElement>(null);
  const filterButton = useRef(null);

  // Close filter dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (filterRef.current && !filterRef.current.contains(event.target as Node)) {
        setShowFilter(false);
      }
    };

    const handleFilterPositioning = () => {
      if (showFilter && filterRef.current && filterButton.current) {
        const buttonRect = filterButton.current.getBoundingClientRect();
        const filterRect = filterRef.current.getBoundingClientRect();

        let filterTop = buttonRect.bottom;
        const buffer = 25; // buffer for height check
        // If the bottom of the dropdown would be cut off, put the box above, not below
        if (filterTop + filterRect.height > (window.innerHeight - buffer)) {
          filterTop = (buttonRect.top + window.scrollY) - filterRect.height;
          filterRef.current.style.top = `${filterTop}px`;
        }

        // If the right side of the dropdown would be cut off, put the box to the left
        let filterRight = buttonRect.right;
        if (filterRight + filterRect.width > window.innerWidth) {
          filterRight = (buttonRect.right + window.scrollX) - filterRect.width;
          filterRef.current.style.left = `${filterRight}px`;
        }

      }
    };

    handleFilterPositioning();

    if (showFilter) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showFilter, selectedValues]);

  // Filter options based on search term
  const filteredOptions = filterOptions.filter((option) =>
    option.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Handle sort click
  const handleSort = () => {
    onSort(sortKey);
  };

  // Handle filter toggle
  const handleFilterToggle = () => {
    setShowFilter(!showFilter);
  };

  // Handle option selection
  const handleOptionChange = (option: string, checked: boolean) => {
    if (!onFilterChange) return;

    const newValues = checked
      ? [...selectedValues, option]
      : selectedValues.filter((v) => v !== option);

    onFilterChange(newValues);
  };

  // Clear all filters
  const handleClearAll = () => {
    if (onFilterChange) {
      onFilterChange([]);
    }
    if (onRangeChange) {
      onRangeChange(undefined, undefined);
    }
    if (onDateRangeChange) {
      onDateRangeChange(undefined, undefined);
    }
  };

  // Get sort icon
  const getSortIcon = () => {
    if (sortDirection === 'asc') {
      return <ChevronUpIcon className="h-4 w-4 text-onyx-bright-blue" />;
    } else if (sortDirection === 'desc') {
      return <ChevronDownIcon className="h-4 w-4 text-onyx-bright-blue" />;
    }
    return <ChevronUpIcon className="h-4 w-4 text-gray-500" />;
  };

  // Check if filter is active
  const isFilterActive = () => {
    if (filterType === 'multiselect' || filterType === 'searchable') {
      return selectedValues.length > 0;
    }
    if (filterType === 'range') {
      return rangeMin !== undefined || rangeMax !== undefined;
    }
    if (filterType === 'daterange') {
      return dateFrom !== undefined || dateTo !== undefined;
    }
    return false;
  };

  // Render filter dropdown content
  const renderFilterContent = () => {
    if (filterType === 'multiselect' || filterType === 'searchable') {
      return (
        <div className="p-3">
          <div className="flex items-center justify-between mb-3 pb-2 border-b border-gray-100">
            <span className="text-xs onyx-subtitle text-gray-600 uppercase tracking-wider">
              Filter {title}
            </span>
            {selectedValues.length < filteredOptions.length && (
              <button
                onClick={() =>
                  onFilterChange(filteredOptions)}
                className="text-xs text-blue-600 hover:text-blue-800 onyx-subtitle transition-colors"
              >
                Select All
              </button>
            )}
            {selectedValues.length > 0 && (
              <button
                onClick={handleClearAll}
                className="text-xs text-onyx-red hover:text-red-700 hover:bg-red-50 px-2 py-1 rounded-lg onyx-subtitle transition-all duration-200"
              >
                Clear
              </button>
            )}
          </div>

          {filterType === 'searchable' && (
            <div className="relative mb-3">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder={`Search ${title.toLowerCase()}...`}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-8 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-onyx-bright-blue focus:border-onyx-bright-blue onyx-body transition-all duration-200"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-gray-100 transition-all duration-200"
                >
                  <XMarkIcon className="h-3 w-3" />
                </button>
              )}
            </div>
          )}

          <div className="max-h-48 overflow-y-auto">
            {filteredOptions.length === 0 ? (
              <div className="p-3 text-center text-gray-500 onyx-body text-sm">
                {searchTerm ? 'No matching options found' : 'No options available'}
              </div>
            ) : (
              <div className="space-y-1">
                {filteredOptions.map((option) => (
                  <label
                    key={option}
                    className="flex items-center gap-3 px-2 py-2 hover:bg-gray-50 rounded-lg cursor-pointer transition-all duration-200"
                  >
                    <input
                      type="checkbox"
                      checked={selectedValues.includes(option)}
                      onChange={(e) => handleOptionChange(option, e.target.checked)}
                      className="rounded border-gray-300 text-onyx-bright-blue focus:ring-onyx-bright-blue focus:ring-offset-0"
                    />
                    <span className="text-sm text-gray-700 flex-1 onyx-body">{option}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>
      );
    }

    if (filterType === 'range') {
      return (
        <div className="p-3">
          <div className="flex items-center justify-between mb-3 pb-2 border-b border-gray-100">
            <span className="text-xs onyx-subtitle text-gray-600 uppercase tracking-wider">
              {title} Range
            </span>
            {(rangeMin !== undefined || rangeMax !== undefined) && (
              <button
                onClick={handleClearAll}
                className="text-xs text-onyx-red hover:text-red-700 hover:bg-red-50 px-2 py-1 rounded-lg onyx-subtitle transition-all duration-200"
              >
                Clear
              </button>
            )}
          </div>
          <div className="space-y-3">
            <input
              type="number"
              placeholder="Min"
              value={rangeMin || ''}
              onChange={(e) =>
                onRangeChange?.(e.target.value ? Number(e.target.value) : undefined, rangeMax)
              }
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-onyx-bright-blue focus:border-onyx-bright-blue onyx-body transition-all duration-200"
              step="0.1"
            />
            <input
              type="number"
              placeholder="Max"
              value={rangeMax || ''}
              onChange={(e) =>
                onRangeChange?.(rangeMin, e.target.value ? Number(e.target.value) : undefined)
              }
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-onyx-bright-blue focus:border-onyx-bright-blue onyx-body transition-all duration-200"
              step="0.1"
            />
          </div>
        </div>
      );
    }

    if (filterType === 'daterange') {
      return (
        <div className="p-3">
          <div className="flex items-center justify-between mb-3 pb-2 border-b border-gray-100">
            <span className="text-xs onyx-subtitle text-gray-600 uppercase tracking-wider">
              Date Range
            </span>
            {(dateFrom || dateTo) && (
              <button
                onClick={handleClearAll}
                className="text-xs text-onyx-red hover:text-red-700 hover:bg-red-50 px-2 py-1 rounded-lg onyx-subtitle transition-all duration-200"
              >
                Clear
              </button>
            )}
          </div>
          <div className="space-y-3">
            <input
              type="date"
              placeholder="From"
              value={dateFrom || ''}
              onChange={(e) => onDateRangeChange?.(e.target.value || undefined, dateTo)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-onyx-bright-blue focus:border-onyx-bright-blue onyx-body transition-all duration-200"
            />
            <input
              type="date"
              placeholder="To"
              value={dateTo || ''}
              onChange={(e) => onDateRangeChange?.(dateFrom, e.target.value || undefined)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-onyx-bright-blue focus:border-onyx-bright-blue onyx-body transition-all duration-200"
            />
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <th
      ref={headerRef}
      className={`px-6 py-2 text-left text-xs font-medium text-gray-600 uppercase overflow-y-visible tracking-wider ${className}`}
      style={width ? { width } : undefined}
    >
      <div className="space-y-1">
        <div className="onyx-subtitle">{title}</div>

        <div className="flex items-center justify-between">
          {/* Sort Icon (left) */}
          <button
            onClick={handleSort}
            className="p-1 hover:bg-white hover:shadow-sm rounded transition-all duration-200"
            title={`Sort by ${title}`}
          >
            {getSortIcon()}
          </button>

          {/* Filter Icon (right) or spacer */}
          {filterType ? (
            <div ref={filterButton} className="relative">
              <button
                onClick={handleFilterToggle}
                className={`p-1 hover:bg-white hover:shadow-sm rounded transition-all duration-200 ${isFilterActive() ? 'text-onyx-bright-blue bg-blue-50' : 'text-gray-500'
                  }`}
                title={`Filter ${title}`}
              >
                <FunnelIcon className="h-4 w-4" />
              </button>

            </div>
          ) : (
            /* Spacer for consistent layout */
            <div className="w-6 h-6" />
          )}
        </div>
      </div>
      {/* Filter Dropdown */}
      {showFilter && (
        <div
          ref={filterRef}
          style={{
            left: filterButton.current.getBoundingClientRect().left,
            width: headerRef.current.getBoundingClientRect().width
          }}
          className="absolute mt-2 bg-white border border-gray-200 rounded-xl shadow-lg z-50 min-w-64 overflow-x-auto"
        >
          {renderFilterContent()}
        </div>
      )}
    </th>
  );
};

export default ColumnHeader;
